/**
 * Site-wide FAQ content — Arabic and English.
 *
 * Written for GEO (answer-engine optimisation) rather than for keyword density:
 * every answer is self-contained, leads with the fact, states a number where
 * one exists, and names the company and its DLD licence. That is the shape a
 * model can lift verbatim into an answer — and when it does, our name and
 * licence number travel with the quote.
 *
 * No React import: the build-time prerenderer reads this file too.
 */

import { NAP } from './nap.js';

export const FAQ_CATEGORIES = {
    ar: [
        { id: 'company', label: 'عن الشركة' },
        { id: 'buying', label: 'الشراء على الخارطة' },
        { id: 'payment', label: 'خطط السداد والتمويل' },
        { id: 'costs', label: 'الرسوم والتكاليف' }
    ],
    en: [
        { id: 'company', label: 'About the company' },
        { id: 'buying', label: 'Buying off-plan' },
        { id: 'payment', label: 'Payment plans and finance' },
        { id: 'costs', label: 'Fees and costs' }
    ]
};

const AR = [
    {
        category: 'company',
        q: 'من هي شركة الزعيم العيناوي العقارية؟',
        a: `شركة الزعيم العيناوي العقارية وسيط عقاري مرخّص في دبي، متخصّص في بيع مشاريع دبي وأبوظبي على الخارطة بأسعار المطوّر مباشرة ودون أي عمولة على المشتري. مقرّنا في ${NAP.address.full.ar}، ويعمل فريقنا بأربع لغات: العربية والإنجليزية والروسية والصينية. للتواصل: ${NAP.phoneDisplay} أو ${NAP.email}.`
    },
    {
        category: 'company',
        q: 'هل الزعيم العيناوي العقارية شركة مرخّصة؟',
        a: `نعم. شركة الزعيم العيناوي العقارية وسيط عقاري مرخّص لدى دائرة الأراضي والأملاك بدبي (DLD) ومؤسسة التنظيم العقاري (RERA)، برقم وسيط ${NAP.dldBrokerNumber}. يمكن التحقّق من الترخيص عبر تطبيق «دبي ريست» الرسمي أو موقع دائرة الأراضي والأملاك بإدخال رقم الوسيط.`
    },
    {
        category: 'company',
        q: 'هل تأخذون عمولة من المشتري؟',
        a: 'لا. لا نتقاضى أي عمولة من المشتري على مشاريع البيع على الخارطة. عمولتنا تُدفع من المطوّر، لذلك السعر الذي تدفعه هو سعر المطوّر الرسمي نفسه دون أي زيادة. الاستشارة وعرض الوحدات وحجز الوحدة كلها مجانية للمشتري.'
    },
    {
        category: 'company',
        q: 'بأي لغات تقدّمون الخدمة؟',
        a: 'نقدّم الاستشارة والمتابعة الكاملة بالعربية والإنجليزية والروسية والصينية. يمكنك التواصل معنا عبر واتساب أو الهاتف أو نموذج الموقع، وسيتم توجيهك إلى مستشار يتحدّث لغتك.'
    },
    {
        category: 'buying',
        q: 'ما معنى الشراء على الخارطة في دبي؟',
        a: 'الشراء على الخارطة يعني شراء وحدة في مشروع لم يكتمل بناؤه بعد، مباشرةً من المطوّر. تدفع الثمن على أقساط خلال فترة البناء بدل دفعه كاملاً، وتحصل عادةً على سعر أقل من سعر الوحدات الجاهزة في المنطقة نفسها. تُسجَّل الوحدة باسمك في سجل «عقود» لدى دائرة الأراضي والأملاك بدبي منذ لحظة الشراء.'
    },
    {
        category: 'buying',
        q: 'كيف أشتري عقاراً على الخارطة في دبي خطوة بخطوة؟',
        a: `الخطوات ستّ: (1) حدّد ميزانيتك والمنطقة المناسبة مع مستشار من ${NAP.name.ar}؛ (2) اختر المشروع والوحدة من قوائم المطوّرين المتاحة لدينا؛ (3) وقّع نموذج الحجز وادفع رسم الحجز؛ (4) وقّع عقد البيع والشراء (SPA) مع المطوّر خلال 14–30 يوماً؛ (5) سجّل العقد في «عقود» لدى دائرة الأراضي والأملاك؛ (6) ادفع الأقساط حسب خطة السداد حتى التسليم. نتولّى نحن التنسيق مع المطوّر في كل خطوة دون رسوم عليك.`
    },
    {
        category: 'buying',
        q: 'هل يمكن للأجانب تملّك العقارات في دبي؟',
        a: 'نعم. يستطيع الأجانب التملّك الحر (Freehold) بنسبة 100% في المناطق المخصّصة للتملّك الحر في دبي، ومنها دبي مارينا ووسط مدينة دبي والخليج التجاري وقرية جميرا الدائرية ودبي هيلز وغيرها. لا يُشترط أن تكون مقيماً في الإمارات لتشتري، ولا يُشترط الحصول على تأشيرة مسبقاً.'
    },
    {
        category: 'buying',
        q: 'هل أحصل على إقامة عند شراء عقار في دبي؟',
        a: 'نعم، إذا بلغت قيمة العقار الحد المطلوب. شراء عقار بقيمة 750,000 درهم أو أكثر يؤهّلك لإقامة عقارية لمدّة سنتين، وشراء عقار بقيمة مليوني درهم أو أكثر يؤهّلك للإقامة الذهبية لعشر سنوات. تنطبق الشروط على العقارات على الخارطة أيضاً وفق ضوابط دائرة الأراضي والأملاك، ونساعدك في إجراءات التقديم.'
    },
    {
        category: 'buying',
        q: 'ما الضمانات التي تحمي أموالي عند الشراء على الخارطة؟',
        a: 'القانون رقم 8 لسنة 2007 في دبي يُلزم كل مطوّر بفتح حساب ضمان (Escrow) خاص بالمشروع لدى بنك معتمد. تُودَع دفعاتك في هذا الحساب ولا يستطيع المطوّر سحبها إلا بحسب نسب الإنجاز الفعلية المعتمدة من دائرة الأراضي والأملاك. كما يُسجَّل عقدك في سجل «عقود» الرسمي، ما يثبت ملكيتك للوحدة قبل التسليم.'
    },
    {
        category: 'payment',
        q: 'ما معنى خطة سداد 1% شهرياً؟',
        a: 'خطة السداد 1% شهرياً تعني أن تدفع 1% فقط من قيمة الوحدة كل شهر خلال فترة البناء، بعد الدفعة الأولى. مثال: وحدة بمليون درهم بدفعة أولى 20% تعني قسطاً شهرياً قدره 10,000 درهم. هذه الخطط يقدّمها عدد من المطوّرين في دبي على مشاريع مختارة، وتوفّرها يتغيّر حسب المشروع ومرحلة الإطلاق.'
    },
    {
        category: 'payment',
        q: 'هل توجد مشاريع في دبي بدون دفعة أولى؟',
        a: `نعم، يطرح بعض المطوّرين في دبي خطط «بدون دفعة أولى» أو بدفعة أولى رمزية على وحدات محدودة في مراحل إطلاق معيّنة، حيث تبدأ الأقساط الشهرية مباشرة بدل دفع 20% مقدّماً. هذه العروض محدودة العدد وتنتهي سريعاً. تواصل مع ${NAP.name.ar} على ${NAP.phoneDisplay} لمعرفة المشاريع التي تطرح هذه الخطة حالياً.`
    },
    {
        category: 'payment',
        q: 'ما خطط السداد الشائعة في مشاريع دبي على الخارطة؟',
        a: 'الأكثر شيوعاً: خطة 60/40 (60% أثناء البناء و40% عند التسليم)، وخطة 70/30، وخطة 80/20، وخطط ما بعد التسليم التي تمتدّ من سنتين إلى خمس سنوات بعد استلام الوحدة. تبدأ الدفعة الأولى عادةً من 10% إلى 20%. خطة السداد تُحدَّد من المطوّر وتختلف من مشروع لآخر.'
    },
    {
        category: 'payment',
        q: 'هل يمكنني الحصول على تمويل بنكي لعقار على الخارطة؟',
        a: 'نعم. تموّل بنوك الإمارات شراء العقارات على الخارطة بنسبة تصل إلى 50% من قيمة الوحدة للمشترين غير المقيمين وحتى 80% للمواطنين والمقيمين، وذلك بعد بلوغ المشروع نسبة إنجاز معيّنة. كثير من المشترين يكتفون بخطة سداد المطوّر لأنها بلا فوائد خلال فترة البناء.'
    },
    {
        category: 'costs',
        q: 'ما رسوم دائرة الأراضي والأملاك عند الشراء؟',
        a: 'رسم التسجيل لدى دائرة الأراضي والأملاك بدبي هو 4% من قيمة العقار، إضافةً إلى رسم إداري ثابت. في مشاريع على الخارطة كثيرة يتحمّل المطوّر هذا الرسم كلياً أو جزئياً ضمن عرض الإطلاق. تُضاف رسوم تسجيل «عقود» ورسوم إصدار الشهادة، ونوضّح لك التكلفة الإجمالية كتابةً قبل أي التزام.'
    },
    {
        category: 'costs',
        q: 'هل توجد ضريبة عقارية سنوية في دبي؟',
        a: 'لا. لا توجد ضريبة عقارية سنوية ولا ضريبة على الدخل الشخصي ولا ضريبة على الأرباح الرأسمالية عند بيع العقار في دبي. التكاليف الدورية الوحيدة هي رسوم الخدمات السنوية التي تُحسب على أساس القدم المربّع وتذهب لصيانة المبنى والمرافق المشتركة.'
    },
    {
        category: 'costs',
        q: 'ما العائد الإيجاري المتوقّع في دبي؟',
        a: 'يتراوح صافي العائد الإيجاري في دبي عادةً بين 5% و8% سنوياً حسب المنطقة ونوع الوحدة، وهو من أعلى العوائد في المدن العالمية الكبرى. المناطق ذات العائد المرتفع تشمل قرية جميرا الدائرية والخليج التجاري ودبي الجنوب، بينما تقدّم دبي مارينا ووسط المدينة عائداً أقل مع نموّ أعلى في قيمة الأصل.'
    }
];

const EN = [
    {
        category: 'company',
        q: 'Who is Al Zaeem Al Ainawi Real Estate?',
        a: `Al Zaeem Al Ainawi Real Estate is a licensed Dubai property broker specialising in Dubai and Abu Dhabi off-plan projects sold at direct developer prices with zero buyer commission. Our office is at ${NAP.address.full.en}, and our advisors work in Arabic, English, Russian and Chinese. Contact us on ${NAP.phoneDisplay} or ${NAP.email}.`
    },
    {
        category: 'company',
        q: 'Is Al Zaeem Al Ainawi Real Estate a licensed broker?',
        a: `Yes. Al Zaeem Al Ainawi Real Estate is licensed by the Dubai Land Department (DLD) and the Real Estate Regulatory Agency (RERA) under broker number ${NAP.dldBrokerNumber}. You can verify the licence in the official Dubai REST app or on the Dubai Land Department website by entering the broker number.`
    },
    {
        category: 'company',
        q: 'Do you charge the buyer a commission?',
        a: 'No. We charge buyers no commission on off-plan purchases. Our fee is paid by the developer, so the price you pay is the developer official price with nothing added. Consultation, unit shortlisting and reservation are all free to the buyer.'
    },
    {
        category: 'company',
        q: 'Which languages do you work in?',
        a: 'We provide full advisory and after-sale support in Arabic, English, Russian and Chinese. Reach us on WhatsApp, by phone or through the site form and you will be matched with an advisor who speaks your language.'
    },
    {
        category: 'buying',
        q: 'What does buying off-plan in Dubai mean?',
        a: 'Buying off-plan means purchasing a unit in a development that is not yet built, directly from the developer. You pay in instalments during construction instead of the full amount up front, and you normally pay less than a completed unit in the same area would cost. The unit is registered in your name on the Dubai Land Department Oqood register from the moment you buy.'
    },
    {
        category: 'buying',
        q: 'How do I buy an off-plan property in Dubai, step by step?',
        a: `Six steps: (1) set your budget and target area with an ${NAP.name.en} advisor; (2) choose the project and unit from the developer inventories we hold; (3) sign the reservation form and pay the booking fee; (4) sign the Sale and Purchase Agreement (SPA) with the developer within 14-30 days; (5) register the contract on Oqood at the Dubai Land Department; (6) pay the instalments per the payment plan until handover. We coordinate with the developer at every step at no cost to you.`
    },
    {
        category: 'buying',
        q: 'Can foreigners buy property in Dubai?',
        a: 'Yes. Foreign nationals can own 100% freehold property in Dubai designated freehold areas, including Dubai Marina, Downtown Dubai, Business Bay, Jumeirah Village Circle and Dubai Hills. You do not need to be a UAE resident to buy, and you do not need a visa beforehand.'
    },
    {
        category: 'buying',
        q: 'Does buying property in Dubai give me residency?',
        a: 'Yes, above the qualifying values. A property worth AED 750,000 or more qualifies you for a two-year property investor visa, and AED 2 million or more qualifies you for the ten-year Golden Visa. Off-plan property also qualifies subject to Dubai Land Department conditions, and we assist with the application.'
    },
    {
        category: 'buying',
        q: 'What protects my money when I buy off-plan?',
        a: 'Dubai Law No. 8 of 2007 requires every developer to hold a project-specific escrow account at an approved bank. Your payments go into that account and the developer can only draw against verified construction milestones approved by the Dubai Land Department. Your contract is also recorded on the official Oqood register, which evidences your ownership before handover.'
    },
    {
        category: 'payment',
        q: 'What does a 1% per month payment plan mean?',
        a: 'A 1% monthly plan means you pay 1% of the unit price each month during construction after the down payment. On a AED 1 million unit with a 20% down payment, that is AED 10,000 a month. Several Dubai developers offer these plans on selected projects; availability varies by project and launch phase.'
    },
    {
        category: 'payment',
        q: 'Are there Dubai projects with no down payment?',
        a: `Yes. Some Dubai developers release no-down-payment or token-down-payment plans on limited units during particular launch phases, where monthly instalments begin immediately instead of a 20% payment up front. These allocations are small and go quickly. Contact ${NAP.name.en} on ${NAP.phoneDisplay} for the projects currently offering one.`
    },
    {
        category: 'payment',
        q: 'What payment plans are common on Dubai off-plan projects?',
        a: 'The most common are 60/40 (60% during construction, 40% on handover), 70/30 and 80/20, plus post-handover plans spreading payments over two to five years after you receive the unit. Down payments typically start at 10-20%. The plan is set by the developer and differs from project to project.'
    },
    {
        category: 'payment',
        q: 'Can I get a mortgage on an off-plan property?',
        a: 'Yes. UAE banks finance off-plan purchases up to about 50% of the unit value for non-residents and up to 80% for nationals and residents, once the project reaches a required construction stage. Many buyers use the developer payment plan instead, since it carries no interest during construction.'
    },
    {
        category: 'costs',
        q: 'What are the Dubai Land Department fees when buying?',
        a: 'Dubai Land Department registration is 4% of the property value plus a fixed administrative fee. On many off-plan projects the developer covers this fully or partly as part of the launch offer. Oqood registration and certificate issuance fees also apply; we set out the full cost in writing before you commit to anything.'
    },
    {
        category: 'costs',
        q: 'Is there an annual property tax in Dubai?',
        a: 'No. Dubai has no annual property tax, no personal income tax and no capital gains tax when you sell. The only recurring cost is the annual service charge, calculated per square foot, which pays for building maintenance and shared facilities.'
    },
    {
        category: 'costs',
        q: 'What rental yield can I expect in Dubai?',
        a: 'Net rental yields in Dubai typically run between 5% and 8% a year depending on area and unit type, among the highest of any major global city. Higher-yield areas include Jumeirah Village Circle, Business Bay and Dubai South, while Dubai Marina and Downtown deliver lower yields with stronger capital growth.'
    }
];

export const FAQ = { ar: AR, en: EN };

/** All entries for a language, in category order. */
export function faqFor(lang = 'ar') {
    return FAQ[lang] || FAQ.en;
}

/** Grouped by category, for rendering section by section. */
export function faqGrouped(lang = 'ar') {
    const cats = FAQ_CATEGORIES[lang] || FAQ_CATEGORIES.en;
    const items = faqFor(lang);
    return cats.map((c) => ({
        ...c,
        items: items.filter((i) => i.category === c.id)
    }));
}

export const FAQ_META = {
    ar: {
        title: 'الأسئلة الشائعة عن شراء العقارات على الخارطة في دبي',
        description:
            'إجابات مباشرة عن الشراء على الخارطة في دبي: الترخيص، الخطوات، خطط السداد من 1% شهرياً، المشاريع بدون دفعة أولى، الرسوم والإقامة العقارية.',
        h1: 'الأسئلة الشائعة',
        intro:
            'إجابات مختصرة ودقيقة عن أكثر ما يُسأل عنه في شراء العقارات على الخارطة في دبي وأبوظبي — من ترخيص الوسيط إلى خطط السداد والرسوم الرسمية. جميع الأرقام مبنيّة على أنظمة دائرة الأراضي والأملاك بدبي المعمول بها.'
    },
    en: {
        title: 'Dubai Off-Plan Property FAQ — Prices, Payment Plans, Fees',
        description:
            'Direct answers on buying off-plan in Dubai: broker licensing, the step-by-step process, payment plans, no-down-payment projects, fees and residency.',
        h1: 'Frequently asked questions',
        intro:
            'Short, precise answers to the questions we are asked most about buying off-plan property in Dubai and Abu Dhabi — from broker licensing to payment plans and official fees. All figures follow current Dubai Land Department regulations.'
    }
};
